import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subscription } from 'rxjs';
import { DownloadService, IComplianceCheckCounter, IDownloadItems, IStandEquipmentsCounter } from '../../services/download.service';
import { SessionService } from '../../services/session.service';

import { SHARED_MODULES } from '../../../shared.';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { ImageRecognitionService } from '../../services/image-recognition.service';
import { PlanogramsService } from '../../services/planograms.service';
import { Router } from '@angular/router';
import { ServiceExtensions } from '../../models/serviceExtensions';
import { HasExtensionPipe } from '../../pipes/has-extension.pipe';

@Component({
    selector: 'app-download-menu',
    templateUrl: './download-menu.component.html',
	imports: [SHARED_MODULES, PhIconComponent, PhIconWrapperComponent, HasExtensionPipe],
    standalone: true
})
export class DownloadMenuComponent implements OnInit, AfterViewInit, OnDestroy {

	@ViewChild('loadingsMenuTrigger') loadingsMenuTrigger: MatMenuTrigger;

	downloadList: IDownloadItems;
	private notificationSubscription: Subscription;

	protected readonly extensions: typeof ServiceExtensions = ServiceExtensions;

	constructor(
		private router: Router,
		private downloadService: DownloadService,
		private sessionService: SessionService,
		private planogramsService: PlanogramsService,
		private imageRecognitionService: ImageRecognitionService
	) {}

	ngOnInit(): void {
		this.downloadService.onInit();
		this.downloadList = this.downloadService.downloadList;
	}

	ngAfterViewInit(): void {
		this.notificationSubscription = this.downloadService.openNotificationSubject.subscribe(() => {
			if (this.loadingsMenuTrigger?.menuOpen) {
				this.loadingsMenuTrigger.closeMenu();
			}
		});
	}

	ngOnDestroy(): void {
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe();
		}
	}

	calculateProgressStatus(start: number, finish: number): number {
		if (finish === 0) return 0;
		return Math.floor((start / finish) * 100);
	}

	cancelAnalyticsDownload(id: string): void {
		this.downloadService.cancelAnalyticsDownload(id);
	}

	cancelExcelFileDownload(id: string): void {
		this.downloadService.cancelExcelFileDownload(id);
	}

	downloadStandEquipments(item: IStandEquipmentsCounter): void {
		this.downloadService.downloadStandEquipments(item);
	}

	deleteStandEquipments(id: string): void {
		this.downloadService.deleteStandEquipments(id);
	}

	deleteExecution(id?: string): void {
		this.downloadService.deleteExecution(id);
	}

	deleteScheduleExecution(id?: string): void {
		this.downloadService.deleteScheduleExecution(id);
	}

	openExecutionNotification(id?: string): void {
		this.downloadService.openExecutionNotification(id);
	}

	openScheduleExecutionNotification(id?: string): void {
		this.downloadService.openScheduleExecutionNotification(id);
	}

	retryComplianceCheck(item: IComplianceCheckCounter): void {
		this.planogramsService.deleteJob(item.id).subscribe();
		this.downloadService.deleteComplianceCheckFromArray(item.id);
		this.imageRecognitionService.startComplianceCheck(item.planogramId, item.photoreportId).subscribe();
	}

	openPhotoReportPage(item: IComplianceCheckCounter): void {
		const url: string = this.router.serializeUrl(
			this.router.createUrlTree(
				this.sessionService.getLink(['control']),
				{ queryParams: { plan: item.planId, planogram: item.mainPlanogramId, isReturn: false, recognition: true } }
			)
		);
		window.open(url, '_blank');
	}

	shouldShowReadMoreButton(isScheduleExecution: boolean = false): boolean {
		const settings = this.sessionService.personalSettings;
		const notificationsWebDisabled = settings.notificationsWebDisabled || [];
		const notificationsWebPushDisabled = settings.notificationsWebPushDisabled || [];

		const notificationType = isScheduleExecution ? 'scheduled_execution' : 'multi_execution';
		const isDisabled = notificationsWebDisabled.includes(notificationType);
		const isPushDisabled = notificationsWebPushDisabled.includes(notificationType);

		return !isDisabled && !isPushDisabled;
	}
}
